export default {
    props: {
        dependencies: {
            type: Array,
            default: () => [],
        },
        disableUntilReady: {
            type: Boolean,
            default: true,
        },
    },

    computed: {
        allDependenciesSelected() {
            if (!this.dependencies.length) return true;
            return this.dependencies.every(k => {
                const v = this.data?.[k];
                return v !== null && v !== undefined && v !== '';
            });
        },
        dependencyDisabled() {
            return this.disableUntilReady && !this.allDependenciesSelected;
        },
    },

    mounted() {
        // One watcher per dependency
        this.dependencies.forEach(property => {
            this.$watch(
                () => this.data?.[property],
                () => this.handleDependencyChange()
            );
        });
    },

    methods: {
        handleDependencyChange() {
            // For multi-select (selectedValues branch): clear immediately, no option-validity check possible.
            if (!this.property && Array.isArray(this.selectedValues)) {
                this.selectedValues = [];
                this.$emit('change', []);
            }

            // Reset the cached option list so the next load fetches fresh data.
            if (this.property) {
                this.$root.$emit('lazy-select-reset', this.property, this.form);
            }

            // When dependencies are not fully selected the field is disabled — clear the value eagerly
            // and notify listeners so that downstream handlers receive null.
            if (!this.allDependenciesSelected) {
                if (this.property && this.data) {
                    this.clearDependentValue(true);
                }
                return;
            }

            if (typeof this.ensureOptionsLoaded === 'function') {
                // Capture the current value before clearing so we can restore it if still valid.
                const previousValue = (this.property && this.data) ? this.data[this.property] : null;

                // Clear the value eagerly before loading new options. This ensures that when
                // internalOptions is updated by ensureOptionsLoaded, the value prop on SelectSearchable
                // is already null, allowing autoSelectSingleOption to fire correctly.
                // Notify listeners (e.g. onCategorySelected) at this point — before the load — so that
                // downstream handlers receive null before any new selection is made.
                if (previousValue !== null && previousValue !== undefined && previousValue !== '') {
                    this.clearDependentValue(true);
                }

                const loadPromise = this.ensureOptionsLoaded(true); // force reload options

                // ensureOptionsLoaded is async and may return undefined when it short-circuits (no optionsLoader).
                // In that case fall back to the old behaviour of clearing immediately.
                if (loadPromise && typeof loadPromise.then === 'function') {
                    loadPromise.then(() => {
                        // After the new options are available, restore the previous value if still valid.
                        const newOptions = Array.isArray(this.internalOptions) ? this.internalOptions : [];
                        const flatOptions = typeof this.getFlatOptions === 'function'
                            ? this.getFlatOptions(newOptions)
                            : newOptions;
                        const valueStillValid = previousValue !== null
                            && previousValue !== undefined
                            && previousValue !== ''
                            && flatOptions.some(o => o.value === previousValue);

                        if (valueStillValid && this.property && this.data) {
                            // Value is still offered in the new options — restore it.
                            this.$set(this.data, this.property, previousValue);
                        }
                        // If not valid, the eager clear above already handled it.
                    });
                } else {
                    // No promise returned (no optionsLoader configured) — clear as before.
                    if (this.property && this.data) {
                        this.clearDependentValue(true);
                    }
                }
            } else {
                // ensureOptionsLoaded not available — clear as before.
                if (this.property && this.data) {
                    this.clearDependentValue(true);
                }
            }
        },

        // Clears the dependent field value and notifies listeners via the standard select event chain
        // so that @change and @changeOption handlers (e.g. onCategorySelected) receive null rather than
        // a stale value or no event at all.
        // Only emits if the value was actually set — avoids overwriting an auto-selected value that
        // was set after the options loaded but before this method fires.
        clearDependentValue(notifyListeners = false) {
            const hadValue = this.data[this.property] !== null
                && this.data[this.property] !== undefined
                && this.data[this.property] !== '';
            this.$set(this.data, this.property, null);
            if (notifyListeners && hadValue && typeof this.handleSelect === 'function') {
                this.handleSelect(null);
            }
        },
    },
};
